import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { toMessages } from "@/lib/form-errors"
import { brandSettingsCharterValuesPath } from "@/routes"

/**
 * OKS-383 — "Crew values" editor (UX Spec §2.3) — a minimal/textual
 * section below the rich-text charter on Brand Settings → Charter.
 *
 * Three numbered rows; each row = Title input + Body input + an icon
 * `Select` (catalog from PRD §3, plus a "number" option → ordinal
 * badge). One "Save values" button.
 *
 * **Crew form rules:** Inertia `<Form>` from `@inertiajs/react` with
 * plain shadcn `Input` / `Label` / `Select` + `name` attributes; errors
 * render from the render-prop `errors` object (dotted keys
 * `charter_values.0.title`). NO shadcn `Form`/`FormField`, NO
 * react-hook-form, NO zod.
 *
 * **Wire shape:** `charter_values[i][title|body|icon_key]` — the
 * rank-ladder array idiom (`permit(charter_values: […])` coerces to an
 * ordered array server-side). Title/body are uncontrolled native inputs;
 * the Radix `Select` is controlled (it has no native form value), so a
 * hidden `<input>` mirrors the selected icon into the submission.
 */

// Radix SelectItem can't use an empty-string value — sentinel for the
// "no icon / ordinal badge" choice, mapped back to "" in the hidden input.
const ICON_NONE = "__none__"

export interface CharterValueRow {
  title: string
  body: string
  icon_key: string
}

export interface CharterValuesEditorProps {
  values: CharterValueRow[]
  /** Catalog icon keys (server-provided — `Brand::CHARTER_VALUE_ICON_KEYS`). */
  icons: string[]
  /** Server-enforced soft caps, mirrored as `maxLength` (defence-in-depth). */
  maxLengths: { title: number; body: number }
  canEdit: boolean
}

export function CharterValuesEditor({
  values,
  icons,
  maxLengths,
  canEdit,
}: CharterValuesEditorProps) {
  const { t } = useTranslation()
  const [iconKeys, setIconKeys] = useState<string[]>(
    values.map((v) => (v.icon_key ? v.icon_key : ICON_NONE)),
  )

  function setIconAt(index: number, value: string) {
    setIconKeys((prev) => prev.map((key, i) => (i === index ? value : key)))
  }

  return (
    <div data-testid="charter-values-editor" className="space-y-6">
      <HeadingSmall
        title={t("settings.brand.charter.values.title")}
        description={t("settings.brand.charter.values.description")}
      />

      <p
        data-testid="charter-values-fallback-hint"
        className="text-muted-foreground text-sm"
      >
        {t("settings.brand.charter.values.fallback_hint")}
      </p>

      {!canEdit && (
        <p
          data-testid="charter-values-read-only-hint"
          className="text-muted-foreground text-sm"
        >
          {t("settings.brand.charter.values.read_only_hint")}
        </p>
      )}

      <Form
        method="patch"
        action={brandSettingsCharterValuesPath()}
        className="space-y-6"
        data-testid="charter-values-form"
      >
        {({ processing, errors }) => (
          <>
            {values.map((row, index) => {
              const titleErrors = toMessages(
                errors[`charter_values.${index}.title`],
              )
              const bodyErrors = toMessages(
                errors[`charter_values.${index}.body`],
              )
              const iconErrors = toMessages(
                errors[`charter_values.${index}.icon_key`],
              )
              const titleId = `charter-value-${index}-title`
              const bodyId = `charter-value-${index}-body`
              const iconValue = iconKeys[index] ?? ICON_NONE

              return (
                <fieldset
                  key={index}
                  disabled={!canEdit}
                  data-testid={`charter-value-row-${index}`}
                  className="border-border/60 flex flex-col gap-3 rounded-lg border p-4"
                >
                  <legend className="text-foreground px-1 text-sm font-medium">
                    {t("settings.brand.charter.values.row_label", {
                      number: index + 1,
                    })}
                  </legend>

                  <div className="grid gap-2">
                    <Label htmlFor={titleId}>
                      {t("settings.brand.charter.values.title_label")}
                    </Label>
                    <Input
                      id={titleId}
                      name={`charter_values[${index}][title]`}
                      defaultValue={row.title}
                      maxLength={maxLengths.title}
                      placeholder={t(
                        "settings.brand.charter.values.title_placeholder",
                      )}
                      aria-invalid={titleErrors ? "true" : undefined}
                      data-testid={`charter-value-${index}-title`}
                    />
                    <InputError messages={titleErrors} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor={bodyId}>
                      {t("settings.brand.charter.values.body_label")}
                    </Label>
                    <Input
                      id={bodyId}
                      name={`charter_values[${index}][body]`}
                      defaultValue={row.body}
                      maxLength={maxLengths.body}
                      placeholder={t(
                        "settings.brand.charter.values.body_placeholder",
                      )}
                      aria-invalid={bodyErrors ? "true" : undefined}
                      data-testid={`charter-value-${index}-body`}
                    />
                    <InputError messages={bodyErrors} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor={`charter-value-${index}-icon`}>
                      {t("settings.brand.charter.values.icon_label")}
                    </Label>
                    <Select
                      items={[
                        {
                          value: ICON_NONE,
                          label: t("settings.brand.charter.values.icon_none"),
                        },
                        ...icons.map((key) => ({
                          value: key,
                          label: t(
                            `settings.brand.charter.values.icons.${key}`,
                          ),
                        })),
                      ]}
                      value={iconValue}
                      onValueChange={(value) => setIconAt(index, value ?? "")}
                      disabled={!canEdit}
                    >
                      <SelectTrigger
                        id={`charter-value-${index}-icon`}
                        data-testid={`charter-value-${index}-icon`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ICON_NONE}>
                          {t("settings.brand.charter.values.icon_none")}
                        </SelectItem>
                        {icons.map((key) => (
                          <SelectItem key={key} value={key}>
                            {t(`settings.brand.charter.values.icons.${key}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Mirror the controlled Select into the submission. */}
                    <input
                      type="hidden"
                      name={`charter_values[${index}][icon_key]`}
                      value={iconValue === ICON_NONE ? "" : iconValue}
                    />
                    <InputError messages={iconErrors} />
                  </div>
                </fieldset>
              )
            })}

            {canEdit && (
              <Button
                type="submit"
                disabled={processing}
                data-testid="charter-values-submit"
              >
                {processing && <Spinner className="size-4" />}
                {processing
                  ? t("settings.brand.charter.values.saving")
                  : t("settings.brand.charter.values.save")}
              </Button>
            )}
          </>
        )}
      </Form>
    </div>
  )
}

export default CharterValuesEditor
